import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Button,
  TextField,
  Select,
  Divider,
  IndexTable,
  Spinner,
  Modal,
  Link,
  Tooltip,
  Pagination,
} from '@shopify/polaris';
import apiClient from '../utils/api';
import { refundsOffEverywhere } from '../utils/refund-switch';
import SharedInfoTip from './InfoTip';
import SkuMapTable from './SkuMapTable';
import RefundsCell from './RefundsCell';
import { WRITE_REFUND_CONFIRM, WRITE_REFUND_TIP, refundActionFor } from '../utils/refund-status';
import { headingsFor, emptyRow, DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';
import { useJobToggles } from '../hooks/useJobToggles';
import { trackingPushUnavailable } from '../utils/tracking-push';

// Page size for the Mirakl orders table. The API's `total` is displayed alongside the page so
// the two are never confused (see fetchOrders).
//
// Now the SEED for `ordersPageSize` rather than the size itself: that table pages SERVER-side,
// so the count an admin picks has to go out as the request's `limit` — slicing the response
// would show 10 of the 20 rows the server sent under a page count taken from the full total.
// The three other tables on this tab hold their whole list and slice locally; each keeps its
// own size, because they are read separately and one shared value would resize a table nobody
// touched.
const ORDERS_PAGE_SIZE = DEFAULT_PAGE_SIZE;

// STATUS_BADGE and SYNC_STATUS_RANK removed with the brand-level status badge they fed.
//
// Worth keeping on the record, since the next person to add a syncStatus badge here will need
// it: the values the backend actually writes are completed, completed_with_errors, disabled,
// error, failed, pending, queued and running, plus idle as the schema default. A map missing
// error, disabled or pending lets a connection sitting in `error` fall through to the default
// and render as Idle, which is how that bug appeared the first time. `isBusy` below reads
// syncStatus directly and only cares about running/queued.
//
// Connector.jsx keeps its own copies for the retailer-account screens.

/**
 * Small info affordance, matching the one on the merchant Dashboard.
 *
 * Deliberately Tooltip + a subdued glyph rather than Polaris Icon + InfoIcon: this file imports
 * neither, and a tooltip is the whole point — the icon would be new surface for no gain.
 */
// ConnectorInfoTip moved to components/InfoTip.jsx — one of three identical copies.

/**
 * Money, in the order's OWN currency.
 *
 * Every money figure in this file was written as `${(n || 0).toFixed(2)}` — a hardcoded
 * dollar sign. Two of them sat next to `{currencyCode}`, so a euro-denominated retailer
 * account rendered "$100.00 EUR". A Mirakl account is not necessarily USD: MiraklOrder
 * carries its own currencyCode (schema.prisma:1289), and the global Retailer Orders list
 * already formats through this same call (Connector.jsx:848) — this file was the outlier.
 *
 * A line item carries no currency of its own, so callers pass the order's.
 */
/**
 * Column definitions for this tab's four tables, each with a tip.
 *
 * See utils/table.jsx for why an empty table needs a synthetic row to keep its headings.
 */
/**
 * Said the same way under both forms, because the field means the same thing in both and the
 * one question it raises is what happens when it is left alone.
 *
 * The example lives in the PLACEHOLDER, where it is read while the box is empty — which is
 * the only moment anyone needs to be told what shape of value goes in it.
 */
const orderNamePrefixHelp = (inherited, retailerName) => (
  <>
    Define what prefix to use when naming a Shopify order.{' '}
    {inherited ? (
      <>
        Leave it empty and orders use{' '}
        <Text as="span" fontWeight="semibold">{retailerName || 'the retailer'}</Text>&rsquo;s own
        prefix <Text as="span" fontWeight="semibold">{inherited}</Text>, following it if that
        account changes. Set one here and this brand uses that instead.
      </>
    ) : (
      <>
        By default the <Text as="span" fontWeight="semibold">Customer Reference</Text> from the
        retailer order itself is used; set a prefix here and that is used instead.
      </>
    )}
  </>
);

/**
 * The INHERITED value, shown rather than filled in.
 *
 * Writing the retailer's prefix into this field would store a copy, and a copy freezes: change
 * the retailer's prefix later and every connection made before it keeps the stale value, which
 * is the opposite of what setting it once on the retailer is for. A greyed placeholder says
 * what will happen without recording anything.
 */
const orderNamePrefixPlaceholder = (inherited) => (inherited
  ? `Using the retailer's prefix: ${inherited}`
  : 'e.g. SPO then Order name #SPO-MiraklOrderId');

const CONNECTION_COLUMNS = [
  { key: 'retailer', title: 'Retailer', tip: 'The retailer account this brand is connected to. A brand can be connected to several.' },
  { key: 'nameOnRetailer', title: 'Brand Name on Retailer', tip: "How the retailer spells this brand in its own offer feed. It has to match, or the retailer's listings cannot be attributed to this brand — which is why it is set per connection rather than taken from the brand's name." },
  { key: 'orderTag', title: 'Order Tag', tip: 'The tag written onto Shopify orders from this connection, and what billing matches to decide which retailer they bill as. It is set per retailer account, on Retailer Accounts, and never per brand: the patterns that read it are keyed by retailer, so brands on one account drifting into different tags would bill at different rates.' },
  // Next to Order Tag on purpose: they are one word apart and do entirely different jobs, so
  // the tips have to say which is which where someone is looking at both.
  { key: 'orderNamePrefix', title: 'Order Name Prefix', tip: 'What Shopify orders from this connection are called — prefix "SPO" names an order #SPO-6672623108156-A. Set per brand, and nothing to do with the Order Tag beside it, which decides what the order bills as. Left empty, it follows the retailer account\u2019s prefix; if that is empty too, the order is named with the reference the retailer sends on it.' },
  // "Marketplace" here too — one vocabulary across both screens, or a reader taught the column
  // on Retailer Accounts has to learn it again here. It names the marketplace the tag bills
  // as, which is not the retailer account it sits on: the account is the Mirakl one, the
  // marketplace is ShopSimon or Macy's.
  { key: 'resolves', title: 'Marketplace', tip: 'Which marketplace this tag currently bills as, and therefore which commission rate its orders are charged at. Matched against the patterns on Marketplace Settings. A tag matching nothing bills as Others, at the wrong rate.' },
  { key: 'status', title: 'Status', tip: 'Whether this connection is enabled, and what its last sync did. A disabled connection keeps its data but is skipped by every scheduled job.' },
  { key: 'lastSync', title: 'Last Sync', tip: 'When orders were last pulled for this connection. It does not cover inventory or tracking, which run on their own schedules.' },
  { key: 'actions', title: 'Actions', tip: 'Changes this one connection. Nothing here writes to the retailer.' },
];

const BRAND_ORDER_COLUMNS = [
  { key: 'miraklId', title: 'Mirakl ID', tip: "The retailer's own id for the order. One customer purchase can be split across several of these." },
  { key: 'retailer', title: 'Retailer', tip: 'The retailer account the order came from.' },
  { key: 'customer', title: 'Customer', tip: "The customer name and email the retailer passed us. Some retailers relay a masked address rather than the shopper's own." },
  { key: 'miraklStatus', title: 'Mirakl Status', tip: "The retailer's own state for the order, as of the last time we read it. It does not change between reads." },
  { key: 'syncStatus', title: 'Sync Status', tip: "2W's own state: whether we have created the order in Shopify, refused it, or not reached it yet." },
  { key: 'items', title: 'Items', tip: "Line count for this brand's slice of the order, not the customer's whole purchase." },
  { key: 'total', title: 'Total', tip: "The retailer's own total for this brand's lines, in the order's own currency." },
  { key: 'refunds', title: 'Refunds', tip: "What the retailer has refunded on this brand's lines, and where 2W is with writing it to the brand's Shopify store. Waiting to write means the next refund run (or Write refunds above) writes it; Refused and Write failed need a person." },
  { key: 'tracking', title: 'Tracking', tip: 'The tracking number recorded from Shopify. Whether the retailer has been told is on the Tracking screen, not here.' },
  { key: 'date', title: 'Date', tip: 'When the retailer placed the order, not when we imported it.' },
  { key: 'actions', title: 'Actions', tip: 'Runs one operation for this single order now. Writes to the retailer refuse while their toggle is off in Sync Settings.' },
];

const LINE_COLUMNS = [
  { key: 'sku', title: 'SKU', tip: "The retailer's offer SKU for the line, which is what we match against the brand's Shopify SKUs." },
  { key: 'product', title: 'Product', tip: "The product title as the retailer holds it, which may differ from the brand's own." },
  { key: 'qty', title: 'Qty', tip: 'Units ordered on this line.' },
  { key: 'unitPrice', title: 'Unit Price', tip: "The MARKETPLACE price, not the brand's own retail price — the two routinely differ, and it is the marketplace price the Shopify order is created at." },
  { key: 'total', title: 'Total', tip: "The retailer's own line total." },
];

const money = (amount, currencyCode) =>
  apiClient.formatCurrency(amount || 0, currencyCode || undefined);

export default function ConnectorTab({ brandId, brandName }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pushingTracking, setPushingTracking] = useState(false);
  const [sweepingRefunds, setSweepingRefunds] = useState(false);
  const [skuMapStats, setSkuMapStats] = useState(null);
  const [refreshingSkuMap, setRefreshingSkuMap] = useState(false);
  // Which write operations Sync Settings currently allows, so a switched-off write button
  // explains itself instead of looking live and failing on click. The server refuses either
  // way; this is the courtesy half.
  const { switchedOffReason } = useJobToggles();
  // Deferred refreshes, cleared on unmount.
  //
  // These are the "check back in 5 seconds" refetches after queueing a background job. Left
  // uncleared they fire after the admin has navigated away — React 18 swallows the setState,
  // so what is left is a pointless request, and on a slow connection several of them.
  const timers = useRef([]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current = []; }, []);
  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };


  // Bumped to make the comparison table refetch. The table owns its own paging, filters and
  // sort, so re-rendering it from here would throw those away — a token it watches keeps the
  // admin's place in the table while still pulling in new figures.
  const [skuRowsToken, setSkuRowsToken] = useState(0);
  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(null);
  // The table fetched page 1 and only page 1, so a brand with 500 synced orders could see 20 of
  // them and no way to reach the rest. The route has accepted `page` all along
  // (admin-connector.js:356).
  const [ordersPage, setOrdersPage] = useState(1);
  // Seeded with this table's own existing count, so nobody's view changes size unasked.
  const [ordersPageSize, setOrdersPageSize] = useState(ORDERS_PAGE_SIZE);
  // Two pieces of state, not one: the input updates per keystroke, the committed value is
  // what fetchOrders depends on, so typing does not fire a server-side page query per key.
  const [orderSearchInput, setOrderSearchInput] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Which order's Sync button is mid-flight. An id rather than a boolean, so pressing one row
  // does not put a spinner on every row.
  const [syncingOrderId, setSyncingOrderId] = useState(null);

  // Which connection's "Create match" is in flight.
  const [fixingAttributionFor, setFixingAttributionFor] = useState(null);

  /**
   * Create the attribution pattern for a connection's tag prefix.
   *
   * Offered on the row that reports the problem. The endpoint refuses rather than overwriting
   * when the prefix already attributes to a different retailer, so the failure message is worth
   * showing verbatim — it names the retailer already using that tag.
   */
  const handleCreateAttribution = async (marketplaceId) => {
    setFixingAttributionFor(marketplaceId);
    setError('');
    try {
      const res = await apiClient.createConnectionAttribution(brandId, marketplaceId);
      if (res?.success) setSuccess(res.message || 'Attribution pattern created');
      else setError(res?.error || 'Could not create the attribution pattern');
    } catch (err) {
      setError(err.message || 'Could not create the attribution pattern');
    }
    setFixingAttributionFor(null);
    fetchMarketplaces();
  };

  /**
   * Accept ONE retailer order on Mirakl.
   *
   * The deliberate counterpart to automatic acceptance being off: this exercises OR21 for a single
   * order without committing to everything else the account has waiting.
   *
   * Acceptance means DECIDE, not agree — the decision is made from Shopify stock, so it can come
   * back refused. The banner reports which way it went rather than announcing success, because a
   * refusal cannot be undone on Mirakl and an admin needs to see it happened.
   */
  const handleAcceptOrder = async (orderId) => {
    if (!window.confirm(
      'Accept this order on the retailer?\n\nThis commits you to fulfilling it. If your Shopify '
      + 'stock cannot cover the order it will be REFUSED instead, which cannot be undone.',
    )) return;

    setSyncingOrderId(orderId);
    setError('');
    setSuccess('');
    try {
      const res = await apiClient.acceptMiraklOrder(orderId);
      if (res?.success && res.decision === 'accepted') setSuccess(res.message);
      else if (res?.success && res.decision === 'refused') setError(res.message);
      else setError(res?.error || 'No decision was reached for this order');
    } catch (err) {
      setError(err.message || 'Acceptance failed');
    }
    setSyncingOrderId(null);
    await Promise.all([fetchOrders(), fetchMarketplaces()]);
    setSelectedOrder((cur) => (cur?.id === orderId ? null : cur));
  };

  /** Push tracking for ONE order — OR23 then OR24. */
  const handlePushTracking = async (orderId) => {
    if (!window.confirm('Tell the retailer this order shipped, with its tracking number?')) return;

    setSyncingOrderId(orderId);
    setError('');
    setSuccess('');
    try {
      // The route returns runOneOrder's own result: { ok, code, message }. Prefer `message` —
      // `code` is an internal token like `broken_link`, which is not an answer for an admin.
      const res = await apiClient.pushOrderTracking(orderId);
      if (res?.ok) setSuccess(res.message || 'Tracking sent to the retailer');
      else setError(res?.message || res?.error || res?.code || 'Tracking push failed');
    } catch (err) {
      setError(err.message || 'Tracking push failed');
    }
    setSyncingOrderId(null);
    await Promise.all([fetchOrders(), fetchMarketplaces()]);
    setSelectedOrder((cur) => (cur?.id === orderId ? null : cur));
  };

  /**
   * Push one Mirakl order to Shopify.
   *
   * The endpoint runs inline, so the response is the outcome — either the Shopify order name or
   * the reason it was refused. Both go straight to the banner the rest of this tab uses; the
   * table is refreshed either way, because a failure still changes the row's status and error.
   */
  const handleSyncOrder = async (orderId) => {
    setSyncingOrderId(orderId);
    setError('');
    try {
      const res = await apiClient.syncSingleMiraklOrder(orderId);
      if (res?.success) setSuccess(res.message || 'Order created in Shopify');
      else setError(res?.error || 'Could not create the Shopify order');
    } catch (err) {
      setError(err.message || 'Could not create the Shopify order');
    }
    setSyncingOrderId(null);

    // fetchOrders, not fetchMarketplaces. The orders table is populated by fetchOrders
    // (setOrders); fetchMarketplaces only reloads the connections. Refreshing the wrong one
    // left the row showing FAILED and its old error after a retry that had actually succeeded —
    // the order was in Shopify and the screen still said it had not been created.
    //
    // Both are refreshed: the connection row carries its own Last Sync.
    await Promise.all([fetchOrders(), fetchMarketplaces()]);

    // The detail panel holds a snapshot taken when the row was clicked, so it cannot be
    // updated by reloading the list. Closed rather than left showing a stale error — the
    // banner already carries the outcome, and reopening the row shows the new state.
    setSelectedOrder((cur) => (cur?.id === orderId ? null : cur));
  };
  // One order's refunds written now - the per-order Write refunds. Same pattern as the push
  // above: the outcome goes in the banner, and the list is reloaded so the badge shows it.
  const handleWriteRefunds = async (orderId) => {
    if (!window.confirm(WRITE_REFUND_CONFIRM)) return;
    setSyncingOrderId(orderId);
    setError('');
    try {
      const res = await apiClient.writeOrderRefunds(orderId);
      if (res?.success) setSuccess(res.message || 'Refund written to Shopify');
      else setError(res?.error || 'Could not write the refund');
    } catch (err) {
      setError(err.message || 'Could not write the refund');
    }
    setSyncingOrderId(null);
    await fetchOrders();
    setSelectedOrder((cur) => (cur?.id === orderId ? null : cur));
  };
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Marketplace connections
  const [brandMarketplaces, setBrandMarketplaces] = useState([]);
  const [allMarketplaces, setAllMarketplaces] = useState([]);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  // Per-connection editing. Until now these four settings could only be chosen at connect
  // time: the backend endpoint accepted them all, but the console only ever sent `enabled`,
  // so changing a connection's auto-accept or tag prefix afterwards was impossible. That is
  // also what kept the brand-level duplicates alive as the only editable path.
  const [editConnection, setEditConnection] = useState(null);
  // No autoAcceptOrders, and no orderTagPrefix. updateBrandMarketplaceConfig posts this object
  // wholesale, so a key left here is written back on every save of an unrelated field — and
  // the order tag is set per retailer account now, where one value serves every brand on it.
  const [editForm, setEditForm] = useState({
    brandNameOnMarketplace: '',
    orderNamePrefix: '',
  });
  const [editSaving, setEditSaving] = useState(false);

  // The connection a Delete button was pressed on, held only so the confirmation can name it.
  // Deliberately not merged into editConnection: the Edit modal's state feeds editForm, which
  // updateBrandMarketplaceConfig posts wholesale, and nothing about deleting belongs in that
  // payload.
  const [deleteConnection, setDeleteConnection] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // No autoAcceptOrders here. Acceptance is switched on system-wide in Connector Settings,
  // and asking again while creating a connection implied this checkbox could enable it —
  // it cannot; global off means nothing accepts whatever a connection says. The per-connection
  // field still exists as a per-brand exclusion once acceptance is on globally, and Edit on a
  // connection is where it is set, deliberately after the connection exists rather than as a
  // decision taken before the retailer has returned a single order.
  const [connectForm, setConnectForm] = useState({
    marketplaceId: '',
    brandNameOnMarketplace: '',
    // NOT prefilled, and there is nothing to prefill it from: the retailer's own order code
    // reaches us only on an order, never from an account-level Mirakl endpoint. Empty means
    // the order keeps the code the retailer sent, which the help text says.
    orderNamePrefix: '',
  });
  const [connectSaving, setConnectSaving] = useState(false);
  // The connections table, also client-side — getBrandMarketplaces returns every connection.
  const [connectionPage, setConnectionPage] = useState(1);
  const [connectionPageSize, setConnectionPageSize] = useState(DEFAULT_PAGE_SIZE);
  // The line items inside the order modal. Component state rather than the modal's, because the
  // table is rendered from an IIFE that cannot hold hooks of its own.
  const [linePage, setLinePage] = useState(1);
  const [linePageSize, setLinePageSize] = useState(DEFAULT_PAGE_SIZE);

  // miraklSyncEnabled is NOT in here. updateConnectorConfig posts formState wholesale, so
  // leaving the key would have written false on every save of the brand name — re-vetoing the
  // brand's schedulers through a field that no longer has a control.
  const [formState, setFormState] = useState({
    miraklBrandName: '',
  });

  const fetchConfig = useCallback(async () => {
    try {
      const data = await apiClient.getConnectorConfig(brandId);
      setConfig(data);
      setFormState({
        miraklBrandName: data.miraklBrandName || '',
      });
    } catch (err) {
      setError('Failed to load connector config');
    }
  }, [brandId]);

  // Brand-scoped, and therefore keyed on brandId.
  //
  // It called the unscoped getSkuMapStats(), which returns every listing link across every
  // retailer account and every brand. On a brand's own tab those totals read as the brand's and
  // contradicted its Catalogue tab. The empty dependency array was part of the same mistake:
  // nothing about the request depended on which brand was open.
  const fetchSkuMapStats = useCallback(async () => {
    try {
      const data = await apiClient.getBrandSkuMapStats(brandId);
      setSkuMapStats(data);
    } catch (err) {
      console.error('Failed to load SKU map stats:', err);
    }
  }, [brandId]);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await apiClient.getConnectorOrders(brandId, {
        limit: ordersPageSize,
        page: ordersPage,
        ...(orderSearch.trim() ? { search: orderSearch.trim() } : {}),
      });
      setOrders(data.orders || []);
      // The response carries the real total alongside the page (mirakl-order-service.js:406);
      // it was discarded, so the heading counted the 20 rows on screen and a brand with 500
      // synced orders read "20 recent orders" forever - indistinguishable from a stalled sync.
      setOrdersTotal(typeof data.total === 'number' ? data.total : null);
    } catch (err) {
      console.error('Failed to load Mirakl orders:', err);
    }
  }, [brandId, ordersPage, ordersPageSize, orderSearch]);

  // 350ms, matching the admin Retailer Orders table so the two behave identically.
  useEffect(() => {
    const t = setTimeout(() => setOrderSearch(orderSearchInput), 350);
    return () => clearTimeout(t);
  }, [orderSearchInput]);

  // A new search invalidates the page number: searching from page 3 would otherwise ask for
  // page 3 of a result that has one page and come back empty. A new ROW COUNT does the same —
  // page 3 of a 20-row window is inside page 2 of a 50-row one — and because fetchOrders
  // depends on both, the size change refetches with the new `limit` on page 1.
  useEffect(() => { setOrdersPage(1); }, [orderSearch, ordersPageSize]);

  // The three client-side tables reset their own page when their size changes, and the line
  // items reset when a DIFFERENT order is opened: a 30-line order read on page 3 would open a
  // two-line one on a page that does not exist, and an out-of-range slice renders the
  // empty-state row over an order that has lines.
  useEffect(() => { setConnectionPage(1); }, [connectionPageSize]);
  useEffect(() => { setLinePage(1); }, [selectedOrder, linePageSize]);

  const fetchMarketplaces = useCallback(async () => {
    try {
      const [connections, mps] = await Promise.all([
        apiClient.getBrandMarketplaces(brandId),
        apiClient.getConnectorMarketplaces(),
      ]);
      setBrandMarketplaces(connections || []);
      setAllMarketplaces(mps || []);
    } catch (err) {
      console.error('Failed to load marketplace connections:', err);
    }
  }, [brandId]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchConfig(),
        fetchSkuMapStats(),
        fetchMarketplaces(),
      ]);
      setLoading(false);
    };
    load();
  }, [fetchConfig, fetchSkuMapStats, fetchMarketplaces]);

  // Orders load on their own, NOT inside the block above.
  //
  // fetchOrders now depends on ordersPage, so leaving it in that dependency array would make
  // every page change re-run the whole tab — config, history, SKU map and connections — behind
  // setLoading(true), flashing the entire screen to a spinner to advance one table. The table
  // carries its own empty state, so it does not need the page-level loading gate.
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.updateConnectorConfig(brandId, formState);
      setSuccess('Connector settings saved');
      await fetchConfig();
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  };

  // Busy if ANY connection is mid-sync. brandMarketplaces is loaded before this runs.
  const isBusy = syncing || pushingTracking
    || brandMarketplaces.some((bm) => bm.syncStatus === 'running' || bm.syncStatus === 'queued');

  const handleSync = async () => {
    setSyncing(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.triggerConnectorSync(brandId);
      setSuccess('Sync triggered — check back shortly');
      later(async () => {
        await fetchConfig();
        setSkuRowsToken((t) => t + 1);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to trigger sync');
    }
    setSyncing(false);
  };

  const handleTrackingPush = async () => {
    setPushingTracking(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.triggerTrackingPush(brandId);
      setSuccess('Tracking push triggered — fulfilled orders will be pushed to Mirakl');
      later(async () => {
        await fetchConfig();
        setSkuRowsToken((t) => t + 1);
      }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to trigger tracking push');
    }
    setPushingTracking(false);
  };

  const handleRefundSync = async () => {
    setSweepingRefunds(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.triggerRefundSync(brandId);
      setSuccess(
        'Refund sweep queued \u2014 refunds the retailers have already issued will be written '
        + 'into this brand\u2019s Shopify store. Watch Sync History for the result.',
      );
      later(async () => { await fetchConfig(); }, 3000);
    } catch (err) {
      setError(err.message || 'Failed to trigger the refund sweep');
    }
    setSweepingRefunds(false);
  };

  /**
   * Read BOTH sides of the stock picture for this brand's retailers.
   *
   * "Refresh SKU Map" beside it re-reads only the Mirakl side. Comparing what a retailer holds
   * against what we hold is meaningless if only one side was refreshed — the two run on
   * unrelated cadences, so the figures would be of different ages.
   *
   * Fires once per connected retailer account, because the fetch is per account: the route
   * queues the offer read for that retailer plus the Shopify catalogue mirror for the brands
   * connected to it.
   */
  const handleFetchInventories = async () => {
    const accounts = brandMarketplaces.filter((bm) => bm.enabled && bm.marketplace?.id);
    if (accounts.length === 0) {
      setError('Connect a retailer first — there is nothing to compare against.');
      return;
    }

    setRefreshingSkuMap(true);
    setError('');
    setSuccess('');
    try {
      const results = await Promise.all(
        accounts.map((bm) => apiClient.fetchAccountInventories(bm.marketplace.id)),
      );
      setSuccess(
        `Fetching stock from ${accounts.length} retailer${accounts.length === 1 ? '' : 's'} and `
        + 'the Shopify catalogue. Figures update once both finish.',
      );
      if (results.some((r) => !r)) setError('One of the fetches did not queue — check the queue.');
      // Both jobs page through their APIs, so the counts do not move immediately.
      later(() => {
        fetchSkuMapStats();
        setSkuRowsToken((n) => n + 1);
      }, 8000);
    } catch (err) {
      setError(err.message || 'Failed to queue the inventory fetch');
    }
    setRefreshingSkuMap(false);
  };

  const handleSkuMapRefresh = async () => {
    setRefreshingSkuMap(true);
    setError('');
    setSuccess('');
    try {
      // The SERVER'S message, not a fixed string. It reports how many accounts were queued
      // and says plainly when none was — this asserted success either way.
      const res = await apiClient.refreshSkuMap();
      setSuccess(res?.message || 'SKU map refresh queued.');
      later(async () => {
        await fetchSkuMapStats();
        setSkuRowsToken((n) => n + 1);
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to refresh SKU map');
    }
    setRefreshingSkuMap(false);
  };

  // Null unless the operation is actually switched off — a settings call that failed must not
  // disable anything (see useJobToggles).
  // Per retailer, not system-wide — both moved onto the retailer because retailers differ.
  //
  // These take the RETAILER, not the order. Three different rows need them and each holds a
  // different thing: an order row has order.marketplace, a connection row has bm.marketplace,
  // and the brand-level button spans every retailer this brand is connected to. Written as
  // one argument so a caller cannot silently pass something that has no retailer on it.
  const acceptOffFor = (mp) => (mp?.acceptOrders === true
    ? null
    : `Accepting orders is switched off for ${mp?.name || 'this retailer'}. `
      + 'Switch it on in that retailer\'s settings.');

  const createOffFor = (mp) => (mp?.createShopifyOrders === true
    ? null
    : `Creating Shopify orders is switched off for ${mp?.name || 'this retailer'}. `
      + 'Switch it on in that retailer\'s settings.');

  /**
   * The brand-level button spans every retailer, so it refuses only when NOT ONE of them
   * allows creation — refusing because a single retailer is off would block a sweep that has
   * real work to do elsewhere.
   */
  const createOffEverywhere = () => {
    const connected = (brandMarketplaces || []).filter((bm) => bm.enabled !== false);
    if (connected.length === 0) return null;
    return connected.some((bm) => bm.marketplace?.createShopifyOrders === true)
      ? null
      : 'Creating Shopify orders is switched off for every retailer this brand is connected to.';
  };
  const trackingOff = switchedOffReason('pushTrackingEnabled', 'Push tracking');

  // Refund writing is the RETAILER's switch, not the connection's. The rule lives in its own
  // file because it decides whether a button that moves money can be pressed, and that is
  // worth asserting without mounting this tab.
  const refundsOff = refundsOffEverywhere(brandMarketplaces);

  /**
   * The retailer currently chosen in the Connect dialog, for the order-name hint.
   *
   * Read from the accounts list the dropdown is already built from, so it re-evaluates as the
   * selection changes and costs no request.
   */
  const connectRetailer = allMarketplaces.find((m) => m.id === connectForm.marketplaceId) || null;
  const inventoryPushOff = switchedOffReason('pushInventoryEnabled', 'Push inventory');
  // Sync now, Full Sync and Push to Shopify all end in the same operation, so one reason
  // covers all three: each either creates Shopify orders or queues the job that does.


  // Retailer filter for the comparison table. Enabled connections only, because the table's
  // rows come from enabled connections — offering a disabled one would filter to nothing and
  // read as "no SKUs listed here".
  const brandRetailerOptions = brandMarketplaces
    .filter((bm) => bm.enabled && bm.marketplace?.id)
    .map((bm) => ({ label: bm.marketplace.name, value: bm.marketplace.id }));

  if (loading) {
    return (
      <InlineStack align="center"><Spinner size="large" /></InlineStack>
    );
  }

  // lastOrderSync is still derived from the connections, not from Brand: the brand-level
  // miraklLastOrderSync column was dropped because nothing wrote it once ingest moved to the
  // account-level sweep, so it showed a timestamp frozen at whatever it last happened to be.
  //
  // The worst-status roll-up that used to sit here went with the badge it fed — Sync History
  // reports run state per job, with the error, which is strictly more than a single word.
  const lastOrderSync = brandMarketplaces
    .map((bm) => bm.lastOrderSync)
    .filter(Boolean)
    .sort()
    .pop() || null;
  const connectionErrors = brandMarketplaces
    .filter((bm) => bm.lastSyncError)
    .map((bm) => `${bm.marketplace?.name || 'Retailer'}: ${bm.lastSyncError}`);

  /**
   * The connections table, paged and CLAMPED.
   *
   * Clamped rather than trusted: the list is refetched after every enable, disable or edit,
   * so it can shrink under a page that was valid when it was chosen. An out-of-range slice is
   * empty, which would render the "no connections yet" row over a brand that still has some.
   *
   * Sync History used to be clamped here too. It owns its own paging now, in
   * SyncHistoryTable, along with the same guard.
   */
  const connectionTotalPages = Math.max(1, Math.ceil(brandMarketplaces.length / connectionPageSize));
  const connectionSafePage = Math.min(connectionPage, connectionTotalPages);
  const visibleConnections = brandMarketplaces.slice(
    (connectionSafePage - 1) * connectionPageSize,
    connectionSafePage * connectionPageSize,
  );

  const isDirty = formState.miraklBrandName !== (config?.miraklBrandName || '');

  return (
    <BlockStack gap="600">
      {error && <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>}
      {success && <Banner tone="success" onDismiss={() => setSuccess('')}>{success}</Banner>}

      {/* Mirakl Configuration, FIRST.

          Nothing else on this tab works until the Mirakl brand name matches the
          product_brand on the retailer's offers — that is the field an incoming order is
          recognised by. A tab that opened with an orders table and buried its one
          prerequisite five cards down had the dependency the wrong way round. */}
      <Card>
        <Box padding="200">
          <BlockStack gap="300">
            {/* Save in the header, labelled "Save". At the foot of the card it sat under a
                trailing InlineStack that left an empty band across the card, and the heading
                already says which settings it saves. */}
            <InlineStack align="space-between" blockAlign="center" gap="400" wrap={false}>
              <Text variant="headingMd" as="h3">Mirakl Configuration</Text>
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                disabled={!isDirty}
              >
                Save
              </Button>
            </InlineStack>
            {/* Both explanations sit BELOW their field as helpText, not in a label tooltip.
            
                I had moved them into tooltips to shorten the card; that was the wrong trade.
                These two lines are what an admin needs while filling the field in, not after
                deciding to look — a tooltip hides the one sentence that stops the brand name
                being typed wrong and the sync switch being misread. Both are rewritten shorter
                instead, which was the actual problem. */}
            <TextField
              label="Mirakl Brand Name"
              helpText="Must match the product_brand on this brand's Mirakl offers, ignoring case. It is how an incoming order is recognised as this brand's."
              value={formState.miraklBrandName}
              onChange={(v) => setFormState((s) => ({ ...s, miraklBrandName: v }))}
              autoComplete="off"
            />
            {/* No "Enable automatic sync" checkbox.
            
                Sync is governed system-wide in Connector Settings, plus the per-retailer
                switches on a retailer account and the connection's own enabled flag. A
                brand-level copy meant four places could veto the same job.
            
                The brand column it wrote, miraklSyncEnabled, defaulted to FALSE and gated the
                two brand-shaped schedulers — the order-sync job that creates Shopify orders,
                and the tracking push. This checkbox was the only thing that ever wrote it, so
                removing it alone would have left every brand permanently vetoed with nothing
                able to lift it. The column now defaults to true and existing rows were
                backfilled, so it is a no-op unless per-brand control is deliberately
                re-exposed. */}
            {/* Auto-accept, auto-complete and the order tag prefix are per RETAILER
                CONNECTION, not per brand — use Edit on a connection above. They were
                duplicated here as Brand columns that only ever acted as a fallback when a
                connection was missing, and the tag prefix in particular decides commission
                attribution, so having two places to set it was a real hazard. */}
          </BlockStack>
        </Box>
      </Card>

      {/* Connected Retailers, SECOND — the retailers this brand is attached to.
      
          Configuration then connections then orders: name the brand, attach it to a retailer,
          then look at what arrived. Orders were above this, which put the result before the
          two things that produce it. */}
      <Card>
        <Box padding="200">
          <BlockStack gap="300">
            <InlineStack align="space-between">
              <Text variant="headingMd" as="h3">Connected Retailers</Text>
              <Button
                size="slim"
                onClick={() => {
                  setConnectForm({
                    marketplaceId: '',
                    brandNameOnMarketplace: formState.miraklBrandName || '',
                    // Empty, NOT 'mirakl'. The backend defaults this to the retailer's
                    // name, and billing attributes an order by matching this tag against
                    // the marketplace patterns — 'mirakl' matches nothing, so a connection
                    // created with it is billed as "Others" from birth. Sending a
                    // non-empty default here silently defeated that fallback, which is
                    // exactly the bug the warning banner on this same tab reports.
                    orderNamePrefix: '',
                  });
                  setConnectModalOpen(true);
                }}
                disabled={allMarketplaces.filter((m) => m.isActive && !brandMarketplaces.some((bm) => bm.marketplaceId === m.id)).length === 0}
              >
                Connect Retailer
              </Button>
            </InlineStack>
            {/* The tag-resolution warning, stated before the table rather than left to be
                inferred from a column.
 
                This is a money problem, not a cosmetic one. Billing decides which
                retailer an order belongs to by matching its tags against the marketplace
                patterns; when nothing matches it returns "Others" and the order is billed
                at the wrong commission rate, silently. Nothing else in the app surfaces
                that, and the prefix defaults to the literal string "mirakl" — so the
                connection nobody configured is exactly the one that breaks. */}
            {brandMarketplaces.length > 0 && brandMarketplaces.some((bm) => bm.tagResolution) && (() => {
              const unresolved = brandMarketplaces.filter(
                (bm) => bm.tagResolution && bm.tagResolution.state !== 'ok',
              );
              const n = unresolved.length;
              // Nothing at all when every tag resolves. The success banner that stood here
              // said so on every visit, in green, above a table whose Marketplace column says
              // it per row — a permanent reassurance for a state that is simply normal. What
              // is worth a banner is the exception below.
              if (n === 0) return null;
              return (
                <Banner tone="critical">
                  <BlockStack gap="100">
                    <Text variant="bodyMd" fontWeight="semibold">
                      {n} connection{n === 1 ? ' is' : 's are'} tagging orders that billing cannot attribute
                    </Text>
                    <Text variant="bodySm">
                      Orders from {n === 1 ? 'this connection' : 'these connections'} will fall through to{' '}
                      <strong>Others</strong> and be billed at the wrong commission rate. Set the tag on{' '}
                      <strong>Retailer Accounts</strong> to something an active pattern matches, or add a
                      pattern for it on <strong>Marketplace Settings</strong>.
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      {/* Render an empty prefix as empty. Substituting 'mirakl' here sent the
                          admin looking for a stored value that does not exist, in the one place
                          they read to diagnose mis-attributed money. */}
                      {/* The EFFECTIVE tag, the same one the column prints — naming the
                          connection's stored value sent an admin to change a field that is
                          not the one reaching orders. Empty renders as empty: substituting
                          'mirakl' sent them looking for a stored value that does not exist,
                          in the one place they read to diagnose mis-attributed money. */}
                      Affected: {unresolved.map((bm) => `${bm.marketplace?.name || '—'} (${bm.tagResolution?.tag || 'no tag set'})`).join(' · ')}
                    </Text>
                  </BlockStack>
                </Banner>
              );
            })()}

            {/* Same treatment as the orders table: headings stay visible with no rows, because
                IndexTable swaps the entire table for its emptyState at itemCount 0. The columns
                here are the ones that matter most when there is nothing to show — they tell an
                admin what connecting a retailer will actually configure. */}
              <IndexTable
                // Follows the array actually rendered, and forced to 1 when that is empty so
                // IndexTable keeps its heading row rather than swapping the whole table out.
                itemCount={visibleConnections.length || 1}
                headings={headingsFor(CONNECTION_COLUMNS)}
                selectable={false}
              >
                {visibleConnections.length === 0 && (
                  <IndexTable.Row id="no-connections" position={0}>
                    {/* DERIVED, because it was hardcoded and went stale the day a column was
                        added: the empty message spanned 7 of 8 columns, leaving a stray cell
                        beside it. Nothing errors and no test sees it, because no test renders
                        a brand with zero connections. */}
                    <IndexTable.Cell colSpan={CONNECTION_COLUMNS.length}>
                      <Box padding="500">
                        <Text alignment="center" tone="subdued">
                          {/* Not "yet": this row is also what a brand sees after its last
                              connection is deleted, and "yet" told that admin the delete had
                              not taken effect. */}
                          No retailer connections. Connect one to start syncing this brand&rsquo;s orders.
                        </Text>
                      </Box>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                )}
                {visibleConnections.map((bm, i) => (
                  <IndexTable.Row id={bm.id} key={bm.id} position={i}>
                    <IndexTable.Cell>
                      <Text variant="bodyMd" fontWeight="semibold">{bm.marketplace?.name || '—'}</Text>
                    </IndexTable.Cell>
                    <IndexTable.Cell>{bm.brandNameOnMarketplace || '—'}</IndexTable.Cell>
                    {/* The tag every order from this connection is stamped with in
                        Shopify, and what billing matches on to attribute the order. */}
                    <IndexTable.Cell>
                      <BlockStack gap="050">
                        <Text
                          variant="bodySm"
                          tone={bm.tagResolution && bm.tagResolution.state !== 'ok' ? 'critical' : 'subdued'}
                          fontWeight={bm.tagResolution && bm.tagResolution.state !== 'ok' ? 'semibold' : undefined}
                        >
                          {/* The tag ORDERS WILL CARRY, which is the retailer account's
                              wherever it has one — not this connection's stored value, which
                              is what this cell used to print. Showing that value beside a
                              "Marketplace" verdict about a different string is how a green
                              tick came to sit over a tag that matched nothing. */}
                          {bm.tagResolution?.tag
                            ? <code>{bm.tagResolution.tag}</code>
                            : <em>not set</em>}
                        </Text>
                        {/* Where it came from, said only when it is NOT the account's — the
                            normal case needs no caption, and the two exceptions do: a tag
                            left on an old connection, or a bare fall back to the retailer's
                            name because nothing is set anywhere. */}
                        {bm.tagResolution?.source === 'connection' && (
                          <Tooltip content="Set on this connection, and used only because the retailer account has no tag of its own. Set one on Retailer Accounts and it takes over for every brand.">
                            <Text variant="bodySm" tone="caution">from this connection</Text>
                          </Tooltip>
                        )}
                        {bm.tagResolution?.source === 'retailerName' && (
                          <Tooltip content="No tag is set on the retailer account, so orders are tagged with the retailer's name. That usually matches, but it is a fallback rather than a decision — set the tag on Retailer Accounts.">
                            <Text variant="bodySm" tone="subdued">the retailer&rsquo;s name</Text>
                          </Tooltip>
                        )}
                        {/* The caption here read "schema default", keyed on isSchemaDefault.
                            That value is computed as "the tag is literally mirakl", and the
                            tag it is computed on is now the EFFECTIVE one rather than the
                            connection's column — so the words described a condition that is
                            no longer what triggers them. Keyed on the source instead, which
                            says the thing worth acting on: nothing is set anywhere, so orders
                            carry a string that matches no retailer and bill as Others. */}
                        {bm.tagResolution?.source === 'unset' && (
                          <Tooltip content="No tag on the retailer account, none on this connection, and no retailer name to fall back to. Orders from this connection match no retailer pattern and will bill as Others at the wrong rate.">
                            <Text variant="bodySm" tone="critical">nothing set</Text>
                          </Tooltip>
                        )}
                      </BlockStack>
                    </IndexTable.Cell>

                    {/* What this brand's Shopify orders from this retailer are CALLED.
                        THREE states, and the middle one is the reason this is not a plain
                        value: a connection with nothing of its own follows the retailer
                        account, so the cell shows the prefix orders will actually carry and
                        says where it came from. A blank there would read as "no prefix". */}
                    <IndexTable.Cell>
                      {bm.orderNamePrefix ? (
                        <Text variant="bodySm"><code>#{bm.orderNamePrefix}-</code></Text>
                      ) : bm.marketplace?.orderNamePrefix ? (
                        <BlockStack gap="050">
                          <Tooltip content={`Not set for this brand, so it follows ${bm.marketplace?.name || 'the retailer'} and changes whenever that account does.`}>
                            <Text variant="bodySm" tone="subdued"><code>#{bm.marketplace.orderNamePrefix}-</code></Text>
                          </Tooltip>
                          <Text variant="bodySm" tone="subdued">from the retailer</Text>
                        </BlockStack>
                      ) : (
                        <Tooltip content="Neither this brand nor the retailer sets one, so each order is named with the reference the retailer sent with it.">
                          <Text variant="bodySm" tone="subdued">Order&rsquo;s own reference</Text>
                        </Tooltip>
                      )}
                    </IndexTable.Cell>

                    {/* Does billing actually attribute this connection's orders to a
                        retailer? Three states, because they need different fixes: no
                        pattern at all, a pattern that exists but is switched off, or a
                        clean match. */}
                    <IndexTable.Cell>
                      {!bm.tagResolution ? (
                        <Text variant="bodySm" tone="subdued">—</Text>
                      ) : bm.tagResolution.state === 'ok' ? (
                        <BlockStack gap="050">
                          <Badge tone="success">{bm.tagResolution.marketplace}</Badge>
                          <Text variant="bodySm" tone="subdued">
                            via {bm.tagResolution.patternType} <code>{bm.tagResolution.patternValue}</code>
                          </Text>
                        </BlockStack>
                      ) : bm.tagResolution.state === 'inactive' ? (
                        <BlockStack gap="050">
                          <Badge tone="warning">Pattern inactive</Badge>
                          <Text variant="bodySm" tone="caution">
                            {bm.tagResolution.marketplace} pattern exists but is switched off
                          </Text>
                        </BlockStack>
                      ) : (
                        <BlockStack gap="100">
                          <Badge tone="critical">No match → Others</Badge>
                          <Text variant="bodySm" tone="critical">
                            No pattern matches this prefix
                          </Text>
                          {/* The fix, offered where the problem is reported. It used to be a
                              dead end: the row said orders would bill as "Others" and the only
                              way out was to build a pattern by hand in Marketplace Settings,
                              matching a prefix from memory.
                              
                              Not offered when there is no prefix, or when it is still the
                              'mirakl' default — every retailer would share that one, so it
                              cannot identify any of them and the server refuses it. A button
                              that can only fail is worse than no button; the prefix has to be
                              set first. */}
                          {bm.tagResolution?.tag && bm.tagResolution.tag.toLowerCase() !== 'mirakl' && (
                          <Button
                            size="micro"
                            loading={fixingAttributionFor === bm.marketplaceId}
                            disabled={fixingAttributionFor !== null}
                            onClick={() => handleCreateAttribution(bm.marketplaceId)}
                          >
                            Create match
                          </Button>
                          )}
                        </BlockStack>
                      )}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Badge tone={bm.enabled ? 'success' : undefined}>
                        {bm.enabled ? 'Active' : 'Disabled'}
                      </Badge>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      {bm.lastOrderSync
                        ? new Date(bm.lastOrderSync).toLocaleDateString()
                        : 'Never'}
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <InlineStack gap="200">
                        <Button
                          size="slim"
                          variant="primary"
                          onClick={async () => {
                            try {
                              const result = await apiClient.fetchBrandMarketplaceOrders(brandId, bm.marketplaceId);
                              setSuccess(`Fetched ${result.totalCreated} new orders from ${bm.marketplace?.name} (${result.totalFetched} total, read-only)`);
                              fetchMarketplaces();
                            } catch (err) {
                              setError(err.message);
                            }
                          }}
                        >
                          Fetch Orders
                        </Button>
                        <Tooltip content={createOffFor(bm.marketplace) || `Fetches orders from ${bm.marketplace?.name || 'this retailer'} and creates the Shopify orders for them.`}>
                          <Button
                            size="slim"
                            disabled={Boolean(createOffFor(bm.marketplace))}
                            onClick={async () => {
                              try {
                                await apiClient.triggerBrandMarketplaceSync(brandId, bm.marketplaceId);
                                setSuccess(`Full sync triggered for ${bm.marketplace?.name}`);
                              } catch (err) {
                                setError(err.message);
                              }
                            }}
                          >
                            Full Sync
                          </Button>
                        </Tooltip>
                        <Button
                          size="slim"
                          onClick={() => {
                            setEditConnection(bm);
                            setEditForm({
                              brandNameOnMarketplace: bm.brandNameOnMarketplace || '',
                              // Null reaches the field as '', which is the same thing to a
                              // TextField and saves back as null — so clearing the box puts
                              // the connection back on the retailer's own reference.
                              orderNamePrefix: bm.orderNamePrefix || '',
                            });
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="slim"
                          tone={bm.enabled ? 'critical' : undefined}
                          onClick={async () => {
                            try {
                              await apiClient.updateBrandMarketplaceConfig(brandId, bm.marketplaceId, {
                                enabled: !bm.enabled,
                              });
                              setSuccess(bm.enabled ? 'Connection disabled' : 'Connection enabled');
                              fetchMarketplaces();
                            } catch (err) {
                              setError(err.message);
                            }
                          }}
                        >
                          {bm.enabled ? 'Disable' : 'Enable'}
                        </Button>
                        {/* Last, and behind a confirmation. Disable sits immediately to its
                            left and is the one an admin usually wants; this removes the row. */}
                        <Button
                          size="slim"
                          variant="primary"
                          tone="critical"
                          onClick={() => setDeleteConnection(bm)}
                        >
                          Delete
                        </Button>
                      </InlineStack>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>

              {/* Both controls in one row, and only once there is something to page through.
                  The pager drops out when everything fits, but the row count stays: it is how
                  someone GETS a second page. `connectionSafePage` drives the label, so a
                  shrunken list cannot read "Page 3 of 1". */}
              {brandMarketplaces.length > 0 && (
                <InlineStack align="space-between" blockAlign="center">
                  <PageSizeSelect value={connectionPageSize} onChange={setConnectionPageSize} />
                  {connectionTotalPages > 1 && (
                    <Pagination
                      hasPrevious={connectionSafePage > 1}
                      onPrevious={() => setConnectionPage(connectionSafePage - 1)}
                      hasNext={connectionSafePage < connectionTotalPages}
                      onNext={() => setConnectionPage(connectionSafePage + 1)}
                      label={`Page ${connectionSafePage} of ${connectionTotalPages}`}
                    />
                  )}
                </InlineStack>
              )}
          </BlockStack>
        </Box>
      </Card>

      {/* Sync Status — one row.
      
          It was four stacked blocks (heading + badge, divider, one stat, two buttons) filling
          most of a screen to say "Never". The heading, the badge, the single stat and both
          actions now share one line, and the divider is gone with nothing left to divide.
      
          Tooltips on the stat and both buttons because none of the three explains itself: "Last
          Order Sync" does not say which direction data moves, and neither button says what it
          talks to or what happens if it is pressed twice. */}
      <Card>
        <Box padding="200">
          <BlockStack gap="300">
            {/* One header row: the section's name, when it last ran, and the two actions.
            
                The separate "Sync Status" heading and its Idle badge are gone. The heading named
                a card that now holds the orders table. The badge summarised the worst syncStatus
                across the brand's connections; run state is still on this tab, per job and with
                its error, in Sync History at the bottom — note it is NOT in the connections
                table's "Status" column, which reports enabled/disabled. */}
            {/* Heading and count on the left with the timestamp UNDER them; buttons on the
                right. Stacking the timestamp above the buttons made the right column two rows
                tall, so blockAlign="center" left a band of empty space above the heading — the
                gap was a side effect of that layout, not padding. */}
            <InlineStack align="space-between" blockAlign="start" gap="400" wrap>
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="baseline">
                  <Text variant="headingMd" as="h3">Mirakl Orders</Text>
                  <Text variant="bodySm" tone="subdued">
                    {ordersTotal !== null && ordersTotal > orders.length
                      ? `Showing ${orders.length} of ${ordersTotal}`
                      : `${orders.length} order${orders.length === 1 ? '' : 's'}`}
                  </Text>
                </InlineStack>
                <InlineStack gap="100" blockAlign="center">
                  <SharedInfoTip content="When this brand's orders were last pulled from its retailers. It does not cover inventory or tracking, which run on their own schedules." />
                  <Text variant="bodySm" tone="subdued">Last order sync</Text>
                  <Text variant="bodySm" fontWeight="semibold">
                    {lastOrderSync ? new Date(lastOrderSync).toLocaleString() : 'Never'}
                  </Text>
                </InlineStack>
              </BlockStack>
                <InlineStack gap="200" blockAlign="center">
                <Tooltip content={createOffEverywhere() || "Fetches this brand's orders from every retailer it is connected to, then creates the Shopify orders. Safe to press again — an order already created is skipped."}>
                  <Button
                    variant="primary"
                    onClick={handleSync}
                    loading={syncing}
                    disabled={!formState.miraklBrandName || isBusy || Boolean(createOffEverywhere())}
                  >
                    Sync now
                  </Button>
                </Tooltip>
                <Tooltip content={trackingOff || "Sends tracking numbers for this brand's shipped orders to the retailers, so they mark them dispatched. Orders already confirmed are not sent twice."}>
                  <Button
                    onClick={handleTrackingPush}
                    loading={pushingTracking}
                    disabled={!formState.miraklBrandName || isBusy || Boolean(trackingOff)}
                  >
                    Push tracking
                  </Button>
                </Tooltip>
                <Tooltip content={refundsOff || 'Writes refunds the retailers have already issued into this brand\u2019s Shopify store, so billing credits the commission back and charges a return fee where goods came back. Only refunds settled on or after each retailer\u2019s handover date are written, and one already written is skipped.'}>
                  <Button
                    onClick={handleRefundSync}
                    loading={sweepingRefunds}
                    disabled={isBusy || Boolean(refundsOff)}
                  >
                    Write refunds
                  </Button>
                </Tooltip>
                </InlineStack>
            </InlineStack>
            {connectionErrors.length > 0 && (
              <Banner tone="warning">
                <BlockStack gap="100">
                  {connectionErrors.map((e) => <Text key={e} variant="bodySm">{e}</Text>)}
                </BlockStack>
              </Banner>
            )}

            {/* The headings stay visible when there are no rows.
            
                IndexTable's own `emptyState` cannot do this: it renders `itemCount > 0 ?
                bodyMarkup : emptyState` (IndexTable.js:353), so at zero it replaces the whole
                table, headings included, and an empty table with no column names reads as a
                broken screen rather than an empty one. So itemCount is forced to 1 when there
                are no orders and the message is a single full-width row instead — Cell takes a
                colSpan, and selectable={false} means the inflated count drives nothing else. */}
              <Box maxWidth="320px">
                <TextField
                  label="Search this brand's retailer orders"
                  labelHidden
                  placeholder="Retailer order id, customer, tracking…"
                  value={orderSearchInput}
                  onChange={setOrderSearchInput}
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => setOrderSearchInput('')}
                />
              </Box>
              <IndexTable
                itemCount={orders.length || 1}
                headings={headingsFor(BRAND_ORDER_COLUMNS)}
                selectable={false}
              >
                {orders.length === 0 && (
                  <IndexTable.Row id="no-orders" position={0}>
                    {/* Derived for the same reason as the connections empty row above. */}
                    <IndexTable.Cell colSpan={BRAND_ORDER_COLUMNS.length}>
                      <Box padding="500">
                        <Text alignment="center" tone="subdued">
                          {orderSearch.trim()
                            ? 'No order matches that search. It covers the retailer order id, the commercial id, the customer reference, the Shopify order id, the tracking number, and the customer name or email — not SKUs.'
                            : 'No Mirakl orders synced yet. Press Sync now to fetch this brand\u2019s orders.'}
                        </Text>
                      </Box>
                    </IndexTable.Cell>
                  </IndexTable.Row>
                )}
                {orders.map((order, i) => {
                  const syncTone = order.status === 'SYNCED' ? 'success'
                    : order.status === 'TRACKING_PUSHED' ? 'success'
                    : order.status === 'FULFILLED' ? 'success'
                    : order.status === 'FAILED' ? 'critical'
                    : order.status === 'SKIPPED' ? 'warning'
                    : 'attention';
                  const miraklTone = ['SHIPPING', 'SHIPPED', 'RECEIVED'].includes(order.miraklStatus) ? 'success'
                    : ['CANCELED', 'REFUSED'].includes(order.miraklStatus) ? 'critical'
                    : 'attention';
                  return (
                    <IndexTable.Row
                      id={order.id}
                      key={order.id}
                      position={i}
                      onClick={() => setSelectedOrder(order)}
                    >
                      <IndexTable.Cell>
                        <Text variant="bodyMd" fontWeight="semibold">{order.miraklOrderId}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {order.marketplace ? (
                          <Badge>{order.marketplace.name}</Badge>
                        ) : (
                          <Text variant="bodySm" tone="subdued">—</Text>
                        )}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{order.customerName || order.customerEmail || '—'}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge tone={miraklTone}>{order.miraklStatus || '—'}</Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge tone={syncTone}>{order.status}</Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>{order.lineItemCount || 0}</IndexTable.Cell>
                      <IndexTable.Cell>
                        {/* Was `${n.toFixed(2)} {order.currencyCode}` — so a euro account
                            rendered "$100.00 EUR", asserting one currency while printing the
                            name of another. The formatter emits the correct symbol, which
                            makes the trailing code redundant. */}
                        {money(order.totalPrice, order.currencyCode)}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <RefundsCell refunds={order.refunds} />
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {order.trackingNumber ? (
                          <Badge tone="success">{order.carrier || 'Shipped'}</Badge>
                        ) : order.trackingPushedAt ? (
                          <Badge tone="success">Pushed</Badge>
                        ) : (
                          <Text variant="bodySm" tone="subdued">—</Text>
                        )}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {/* Per order, not per connection. Full Sync above takes everything
                            eligible for a retailer; this pushes ONE — for retrying a single
                            failure without touching the rest, and for following one order
                            through the whole path.
                            
                            Offered only while the order can actually be created: already in
                            Shopify, or not yet accepted by the retailer, and there is nothing
                            for the button to do. stopPropagation because the row opens a
                            detail panel. */}
                        {/* One action per stage the order is actually at, so a row never offers
                            a write that cannot apply. Accept only while the retailer is still
                            waiting; push to Shopify only once accepted and paid; push tracking
                            only once a tracking number exists and has not already been sent. */}
                        <InlineStack gap="100" wrap={false}>
                          {order.miraklStatus === 'WAITING_ACCEPTANCE' && (
                            <Tooltip content={acceptOffFor(order?.marketplace) || 'Accepts this order on the retailer, committing you to fulfil it. Writes to Mirakl.'}>
                              <Button
                                size="micro"
                                tone="critical"
                                loading={syncingOrderId === order.id}
                                disabled={syncingOrderId !== null || Boolean(acceptOffFor(order?.marketplace))}
                                onClick={(e) => { e.stopPropagation(); handleAcceptOrder(order.id); }}
                              >
                                Accept
                              </Button>
                            </Tooltip>
                          )}
                          {!order.shopifyOrderId && !order.shopifyOrderGid
                            && ['SHIPPING', 'SHIPPED', 'RECEIVED'].includes(order.miraklStatus) && (
                              <Tooltip content={createOffFor(order?.marketplace) || 'Creates the Shopify order for this one. Writes to Shopify, not to the retailer.'}>
                                <Button
                                  size="micro"
                                  loading={syncingOrderId === order.id}
                                  disabled={syncingOrderId !== null || Boolean(createOffFor(order?.marketplace))}
                                  onClick={(e) => { e.stopPropagation(); handleSyncOrder(order.id); }}
                                >
                                  {order.status === 'FAILED' ? 'Retry' : 'Push to Shopify'}
                                </Button>
                              </Tooltip>
                            )}
                          {order.trackingNumber && !order.trackingPushedAt && (
                            <Tooltip content={trackingPushUnavailable(order, trackingOff) || 'Tells the retailer this order shipped, with its tracking number. Writes to Mirakl.'}>
                              <Button
                                size="micro"
                                tone="critical"
                                loading={syncingOrderId === order.id}
                                disabled={syncingOrderId !== null || Boolean(trackingPushUnavailable(order, trackingOff))}
                                onClick={(e) => { e.stopPropagation(); handlePushTracking(order.id); }}
                              >
                                Push tracking
                              </Button>
                            </Tooltip>
                          )}
                          {refundActionFor(order.refunds) && (
                            <Tooltip content={WRITE_REFUND_TIP}>
                              <Button
                                size="micro"
                                loading={syncingOrderId === order.id}
                                disabled={syncingOrderId !== null}
                                onClick={(e) => { e.stopPropagation(); handleWriteRefunds(order.id); }}
                              >
                                {refundActionFor(order.refunds).label}
                              </Button>
                            </Tooltip>
                          )}
                          {order.miraklStatus !== 'WAITING_ACCEPTANCE'
                            && (order.shopifyOrderId || order.shopifyOrderGid)
                            && !(order.trackingNumber && !order.trackingPushedAt)
                            && !refundActionFor(order.refunds) && (
                              <Text variant="bodySm" tone="subdued">—</Text>
                            )}
                        </InlineStack>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  );
                })}
              </IndexTable>

            {/* Both controls in one row. `total` and the CHOSEN row count give the page
                count; the route paginates server-side, so changing either the page or the size
                refetches rather than slicing what is already loaded. The pager drops out when
                everything fits, but the row count stays — it is how someone GETS a second
                page, so hiding it at size 50 would strand them there. */}
            {orders.length > 0 && (
              <InlineStack align="space-between" blockAlign="center">
                <PageSizeSelect value={ordersPageSize} onChange={setOrdersPageSize} />
                {ordersTotal !== null && ordersTotal > ordersPageSize && (
                  <Pagination
                    hasPrevious={ordersPage > 1}
                    onPrevious={() => setOrdersPage((n) => Math.max(1, n - 1))}
                    hasNext={ordersPage < Math.ceil(ordersTotal / ordersPageSize)}
                    onNext={() => setOrdersPage((n) => n + 1)}
                    label={`Page ${ordersPage} of ${Math.ceil(ordersTotal / ordersPageSize)}`}
                  />
                )}
              </InlineStack>
            )}
          </BlockStack>
        </Box>
      </Card>

      {/* Shopify Catalogue card REMOVED.
      
          The brand's own Catalogue tab already shows these figures — SKUs with a SKU, total
          variants, last imported — plus the product table itself and a Resync button. Repeating
          three of them here made Retail Connect longer without telling anyone anything the
          neighbouring tab does not.
      
          The SKU Map card below keeps its own counts, which are NOT the same thing: those
          measure how much of the catalogue is listed at a retailer, which is this tab's subject.
          The card's plumbing went with it: catalogueStatus and syncingCatalogue state,
          fetchCatalogueStatus, handleCatalogueSync, and the mount fetch that populated it. The
          Catalogue tab has its own. */}

      {/* SKU Map */}
      <Card>
        <Box padding="200">
          <BlockStack gap="300">
            {/* Heading and button on the first row, figures on the second.
            
                One row was tried and looked cramped — three label/value pairs inline next to a
                heading and a button read as a sentence of numbers. Two rows, no divider, and a
                tight 050 between each label and its value.
            
                Scoped to THIS brand, via GET /connector/brands/:id/sku-map/stats. It used to
                call the unscoped /connector/sku-map/stats, so the card showed every brand's
                listings on every retailer account under a heading an admin reads as this
                brand's. MiraklSkuMap has no brandId, so scoping matches productBrand against
                the brand's three candidate names — the same rule the order ingest uses. */}
            <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
              <Text variant="headingMd" as="h3">SKU Map</Text>
              <InlineStack gap="200" wrap>
                <Tooltip content="Re-reads this brand's stock from BOTH sides — what each retailer holds and what Shopify holds — so the two figures are of the same age. Reads only.">
                  <Button
                    onClick={handleFetchInventories}
                    loading={refreshingSkuMap}
                    disabled={isBusy}
                    size="slim"
                    variant="primary"
                  >
                    Fetch inventories
                  </Button>
                </Tooltip>
                <Tooltip content="Re-reads only the retailer side: which SKUs each retailer lists, and at what stock and price. Reads only.">
                  <Button
                    onClick={handleSkuMapRefresh}
                    loading={refreshingSkuMap}
                    disabled={isBusy}
                    size="slim"
                  >
                    Refresh SKU Map
                  </Button>
                </Tooltip>
              </InlineStack>
            </InlineStack>
            {skuMapStats ? (
              <InlineStack gap="600" wrap>
                <BlockStack gap="050">
                  <InlineStack gap="100" blockAlign="center">
                    <Text variant="bodySm" tone="subdued">Total SKUs</Text>
                    <SharedInfoTip content="Links between this brand's Shopify SKUs and the same items listed on its retailers. A SKU listed at three retailers counts three times, so this is higher than the number of products the brand sells." />
                  </InlineStack>
                  <Text variant="headingLg">{skuMapStats.totalSkus || 0}</Text>
                </BlockStack>
                <BlockStack gap="050">
                  <InlineStack gap="100" blockAlign="center">
                    <Text variant="bodySm" tone="subdued">Brands Mapped</Text>
                    <SharedInfoTip content="How many brand names this brand's listings are filed under at its retailers. Normally 1 — more means a retailer's offers carry a different spelling, which is worth checking." />
                  </InlineStack>
                  <Text variant="headingLg">{skuMapStats.brandCount || 0}</Text>
                </BlockStack>
                <BlockStack gap="050">
                  <InlineStack gap="100" blockAlign="center">
                    <Text variant="bodySm" tone="subdued">Last Refreshed</Text>
                    <SharedInfoTip content="When this brand's listings were last re-read from its retailers. It refreshes on a schedule, so a SKU listed since then will not appear until the next run or a manual refresh." />
                  </InlineStack>
                  <Text variant="bodyMd">
                    {skuMapStats.lastRefreshed
                      ? new Date(skuMapStats.lastRefreshed).toLocaleString()
                      : 'Never'}
                  </Text>
                </BlockStack>
              </InlineStack>
            ) : (
              <Text tone="subdued">No SKU map data available</Text>
            )}

            {/* The counts above say how much is listed; the table says whether the stock
                agrees. Same component as the Retailer Accounts page, without the Brand
                column — this tab is already one brand. */}
            <SkuMapTable
              brandId={brandId}
              marketplaceOptions={brandRetailerOptions}
              refreshToken={skuRowsToken}
              pushDisabledReason={inventoryPushOff}
            />
          </BlockStack>
        </Box>
      </Card>

      {/* Connect Retailer Modal */}
      <Modal
        open={Boolean(editConnection)}
        onClose={() => setEditConnection(null)}
        title={`Edit ${editConnection?.marketplace?.name || 'connection'}`}
        primaryAction={{
          content: 'Save',
          onAction: async () => {
            setEditSaving(true);
            try {
              await apiClient.updateBrandMarketplaceConfig(brandId, editConnection.marketplaceId, {
                brandNameOnMarketplace: editForm.brandNameOnMarketplace,
                // orderTagPrefix is deliberately NOT sent. The route takes it and this object
                // is posted wholesale, so leaving the key would write the old value back on
                // every save of a field that has nothing to do with it.
                orderNamePrefix: editForm.orderNamePrefix,
              });
              setSuccess('Connection updated');
              setEditConnection(null);
              fetchMarketplaces();
            } catch (err) {
              setError(err.message);
            }
            setEditSaving(false);
          },
          loading: editSaving,
          disabled: !editForm.brandNameOnMarketplace,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setEditConnection(null) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <TextField
              label="Brand Name on Retailer"
              helpText="Must match the product_brand field in Mirakl"
              value={editForm.brandNameOnMarketplace}
              onChange={(v) => setEditForm((f) => ({ ...f, brandNameOnMarketplace: v }))}
              autoComplete="off"
            />
            {/* No auto-accept checkbox.
            
                Acceptance is switched on system-wide in Connector Settings, and again per
                retailer account. A third copy here could read "on" while the global switch was
                off, because it showed the connection's own value and consulted neither of the
                other two — so it described a state that did not exist.
            
                The column is still read by the acceptance service, so it keeps its meaning if
                per-brand control is ever re-exposed. Existing false rows were lifted to true,
                because a false row with no control to change it is an invisible veto. */}
            {/* NO Order Tag field.

                It is a billing key, not a brand preference: billing attributes an order by
                matching this tag against the RETAILER patterns, so five brands on one account
                drifting into five tags means four of them bill at the wrong rate. It is set
                once per account on Retailer Accounts, and the account's value always wins —
                so a box here could only ever record a value that is never used, next to one
                that is. The column in the table shows the tag orders actually carry. */}
            <TextField
              label="Order Name Prefix"
              placeholder={orderNamePrefixPlaceholder(editConnection?.marketplace?.orderNamePrefix)}
              helpText={orderNamePrefixHelp(
                editConnection?.marketplace?.orderNamePrefix,
                editConnection?.marketplace?.name,
              )}
              value={editForm.orderNamePrefix}
              onChange={(v) => setEditForm((f) => ({ ...f, orderNamePrefix: v }))}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Delete confirmation.
          It exists for one specific mistake: reaching for Delete when Disable was meant. The
          two buttons sit next to each other in the row, and only one of them is reversible, so
          the difference is stated here rather than implied. */}
      <Modal
        open={Boolean(deleteConnection)}
        onClose={() => (deleting ? undefined : setDeleteConnection(null))}
        title={`Delete the ${deleteConnection?.marketplace?.name || 'retailer'} connection for ${brandName || 'this brand'}?`}
        primaryAction={{
          content: 'Delete connection',
          destructive: true,
          loading: deleting,
          onAction: async () => {
            const bm = deleteConnection;
            if (!bm) return;
            const retailer = bm.marketplace?.name || 'retailer';
            setDeleting(true);
            setError('');
            setSuccess('');
            try {
              await apiClient.deleteBrandMarketplace(brandId, bm.marketplaceId);
              setDeleteConnection(null);
              setSuccess(
                `Deleted the ${retailer} connection for ${brandName || 'this brand'}. `
                + 'Its scheduled jobs were stopped. Orders already collected were kept.',
              );
              // Refetched rather than spliced out of local state: the row carries server-derived
              // fields (tag resolution, last sync) and the pager clamps off the real length.
              await fetchMarketplaces();
            } catch (err) {
              // The server's own message, not a generic one — a refused delete says WHY, and
              // "could not stop the scheduled jobs" needs a different response from a 404.
              setError(err.message || 'Could not delete the connection');
            }
            setDeleting(false);
          },
        }}
        secondaryActions={[{
          content: 'Cancel',
          disabled: deleting,
          onAction: () => setDeleteConnection(null),
        }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text variant="bodyMd">
              This removes the connection between <strong>{brandName || 'this brand'}</strong> and{' '}
              <strong>{deleteConnection?.marketplace?.name || 'this retailer'}</strong>. It cannot be undone.
            </Text>
            <BlockStack gap="150">
              <Text variant="bodyMd" fontWeight="semibold">What happens</Text>
              <Text variant="bodySm">
                &bull; The connection is removed from this brand. It stops appearing in this table.
              </Text>
              <Text variant="bodySm">
                &bull; Its scheduled jobs stop &mdash; no more order pulls or tracking pushes for{' '}
                {deleteConnection?.marketplace?.name || 'this retailer'} on this brand.
              </Text>
              <Text variant="bodySm">
                &bull; Orders already collected are <strong>kept</strong>, with their history and billing.
                Nothing is deleted from the orders table.
              </Text>
              <Text variant="bodySm">
                &bull; Re-connecting later means setting the retailer brand name again
                {deleteConnection?.brandNameOnMarketplace
                  ? <> (currently <code>{deleteConnection.brandNameOnMarketplace}</code>)</>
                  : null}
                , along with the order tag prefix.
              </Text>
            </BlockStack>
            <Banner tone="warning">
              <BlockStack gap="100">
                <Text variant="bodyMd" fontWeight="semibold">This is not the same as Disable</Text>
                <Text variant="bodySm">
                  If you only mean to <strong>pause</strong> this connection, use <strong>Disable</strong> instead
                  &mdash; it stops the same scheduled jobs but keeps the connection, its retailer brand
                  name and its tag prefix, so it can be switched back on in one click. Delete keeps none
                  of that. Cancel and press Disable if pausing is what you want.
                </Text>
              </BlockStack>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        title="Connect a Retailer"
        primaryAction={{
          content: 'Connect',
          onAction: async () => {
            setConnectSaving(true);
            try {
              await apiClient.connectBrandMarketplace(brandId, connectForm.marketplaceId, {
                brandNameOnMarketplace: connectForm.brandNameOnMarketplace,
                // No orderTagPrefix: unset, the route stores the retailer's own name, which is
                // what its attribution patterns are keyed by.
                orderNamePrefix: connectForm.orderNamePrefix,
              });
              setSuccess('Marketplace connected');
              setConnectModalOpen(false);
              fetchMarketplaces();
            } catch (err) {
              setError(err.message);
            }
            setConnectSaving(false);
          },
          loading: connectSaving,
          disabled: !connectForm.marketplaceId || !connectForm.brandNameOnMarketplace,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setConnectModalOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Select
              label="Retailer"
              options={[
                { label: 'Select retailer...', value: '' },
                ...allMarketplaces
                  .filter((m) => m.isActive && !brandMarketplaces.some((bm) => bm.marketplaceId === m.id))
                  .map((m) => ({ label: m.name, value: m.id })),
              ]}
              value={connectForm.marketplaceId}
              // This used to also prefill a tag prefix with the retailer's name. The server
              // does that now, and only the server: the tag is a billing key set per retailer
              // account, so there is nothing about it for this form to carry.
              onChange={(v) => setConnectForm((f) => ({ ...f, marketplaceId: v }))}
            />
            <TextField
              label="Brand Name on Retailer"
              helpText="Must match the product_brand field in Mirakl"
              value={connectForm.brandNameOnMarketplace}
              onChange={(v) => setConnectForm((f) => ({ ...f, brandNameOnMarketplace: v }))}
              autoComplete="off"
            />
            {/* No Order Tag field here either — see the edit modal. A new connection takes
                the retailer's name, which is the value its patterns are keyed by. */}
            {/* Re-reads as the Retailer dropdown above changes, so the hint always describes
                the account actually selected. `allMarketplaces` is already loaded for that
                dropdown and carries every scalar, so this costs no request. */}
            <TextField
              label="Order Name Prefix"
              placeholder={orderNamePrefixPlaceholder(connectRetailer?.orderNamePrefix)}
              helpText={orderNamePrefixHelp(connectRetailer?.orderNamePrefix, connectRetailer?.name)}
              value={connectForm.orderNamePrefix}
              onChange={(v) => setConnectForm((f) => ({ ...f, orderNamePrefix: v }))}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <Modal
          open={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          title={`Mirakl Order #${selectedOrder.miraklOrderId}`}
          large
        >
          <Modal.Section>
            <BlockStack gap="400">
              {/* IDs */}
              <InlineStack gap="600" wrap>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">Mirakl Order ID</Text>
                  <Text variant="bodyMd" fontWeight="semibold">{selectedOrder.miraklOrderId}</Text>
                </BlockStack>
                {selectedOrder.shopifyDraftOrderId && (
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Shopify Draft</Text>
                    <Text variant="bodyMd">#{selectedOrder.shopifyDraftOrderId}</Text>
                  </BlockStack>
                )}
                {selectedOrder.shopifyOrderId && (
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Shopify Order</Text>
                    <Text variant="bodyMd">#{selectedOrder.shopifyOrderId}</Text>
                  </BlockStack>
                )}
              </InlineStack>
              <Divider />

              {/* Status */}
              <InlineStack gap="400" wrap>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">Mirakl Status</Text>
                  <Badge tone={['SHIPPING', 'SHIPPED', 'RECEIVED'].includes(selectedOrder.miraklStatus) ? 'success' : ['CANCELED', 'REFUSED'].includes(selectedOrder.miraklStatus) ? 'critical' : 'attention'}>
                    {selectedOrder.miraklStatus || '—'}
                  </Badge>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">Sync Status</Text>
                  <Badge tone={['SYNCED', 'TRACKING_PUSHED', 'FULFILLED'].includes(selectedOrder.status) ? 'success' : selectedOrder.status === 'FAILED' ? 'critical' : 'attention'}>
                    {selectedOrder.status}
                  </Badge>
                </BlockStack>
                {selectedOrder.shopifySyncStatus && (
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Draft Status</Text>
                    <Text variant="bodyMd">{selectedOrder.shopifySyncStatus.replace(/_/g, ' ')}</Text>
                  </BlockStack>
                )}
              </InlineStack>
              <Divider />

              {/* Customer */}
              <Text variant="headingSm" as="h4">Customer</Text>
              <InlineStack gap="600" wrap>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">Name</Text>
                  <Text variant="bodyMd">{selectedOrder.customerName || '—'}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">Email</Text>
                  <Text variant="bodyMd">{selectedOrder.customerEmail || '—'}</Text>
                </BlockStack>
              </InlineStack>

              {/* Shipping Address */}
              {selectedOrder.shippingAddress && (
                <>
                  <Divider />
                  <Text variant="headingSm" as="h4">Shipping Address</Text>
                  <Text variant="bodyMd">
                    {[
                      selectedOrder.shippingAddress.firstname,
                      selectedOrder.shippingAddress.lastname,
                    ].filter(Boolean).join(' ')}<br />
                    {selectedOrder.shippingAddress.street_1}
                    {selectedOrder.shippingAddress.street_2 && <><br />{selectedOrder.shippingAddress.street_2}</>}
                    <br />
                    {[
                      selectedOrder.shippingAddress.city,
                      selectedOrder.shippingAddress.state,
                      selectedOrder.shippingAddress.zip_code,
                    ].filter(Boolean).join(', ')}
                    <br />
                    {selectedOrder.shippingAddress.country_iso_code}
                  </Text>
                </>
              )}
              <Divider />

              {/* Line Items */}
              <Text variant="headingSm" as="h4">Line Items</Text>
              {(() => {
                const lines = Array.isArray(selectedOrder.lineItems) ? selectedOrder.lineItems : [];
                // Client-side: the lines arrive on the order itself, so there is nothing to
                // re-request. Clamped for the same reason as the tables above — a new order
                // can carry fewer lines than the page in hand.
                const lineTotalPages = Math.max(1, Math.ceil(lines.length / linePageSize));
                const lineSafePage = Math.min(linePage, lineTotalPages);
                const visibleLines = lines.slice(
                  (lineSafePage - 1) * linePageSize,
                  lineSafePage * linePageSize,
                );
                return (
                <BlockStack gap="300">
                <IndexTable
                  itemCount={visibleLines.length || 1}
                  headings={headingsFor(LINE_COLUMNS)}
                  selectable={false}
                >
                  {visibleLines.length === 0 ? emptyRow(
                    LINE_COLUMNS, 'lines',
                    'No line items recorded for this order. The retailer sent none we could attribute to this brand.',
                  ) : visibleLines.map((line, li) => (
                    <IndexTable.Row id={`line-${li}`} key={li} position={li}>
                      <IndexTable.Cell>
                        <Text variant="bodySm" fontWeight="semibold">{line.offer_sku || '—'}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text variant="bodySm">{line.product_title || line.offer_sku || '—'}</Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>{line.quantity || 1}</IndexTable.Cell>
                      {/* A Mirakl order line carries no currency of its own, so both cells
                          take the ORDER's — which is the only currency the line can be in. */}
                      <IndexTable.Cell>
                        {money(line.unit_price ?? line.price, selectedOrder.currencyCode)}
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        {money(line.total_price, selectedOrder.currencyCode)}
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  ))}
                </IndexTable>
                {lines.length > 0 && (
                  <InlineStack align="space-between" blockAlign="center">
                    <PageSizeSelect value={linePageSize} onChange={setLinePageSize} />
                    {lineTotalPages > 1 && (
                      <Pagination
                        hasPrevious={lineSafePage > 1}
                        onPrevious={() => setLinePage(lineSafePage - 1)}
                        hasNext={lineSafePage < lineTotalPages}
                        onNext={() => setLinePage(lineSafePage + 1)}
                        label={`Page ${lineSafePage} of ${lineTotalPages}`}
                      />
                    )}
                  </InlineStack>
                )}
                </BlockStack>
                );
              })()}
              <Divider />

              {/* Financial */}
              <Text variant="headingSm" as="h4">Financial</Text>
              <InlineStack gap="600" wrap>
                <BlockStack gap="100">
                  <Text variant="bodySm" tone="subdued">Total Price</Text>
                  <Text variant="headingMd">{money(selectedOrder.totalPrice, selectedOrder.currencyCode)}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  {/*
                    "Retailer commission", not "Commission" — because a second number with
                    almost that name exists on Orders Management, and they are not the same
                    thing and can legitimately differ:

                      HERE  MiraklOrder.totalCommission — the retailer's OWN figure, summed
                            from `total_commission` on this brand's matched order lines as
                            OR11 reported them (mirakl-order-service.js:165, :425). It is
                            what the retailer says it is deducting.
                      THERE Order.appliedMarketplaceCommissionAmount — OUR calculation: a
                            rate configured in 2W applied to Shopify's net amount
                            (unified-sync-service.js:1193). It is what we expect the
                            deduction to be.

                    Nothing reconciles the two, so each has to say which it is. This one is
                    the reported figure and is the stronger of the two.
                  */}
                  <InlineStack gap="100" blockAlign="center">
                    <Text variant="bodySm" tone="subdued">Retailer commission</Text>
                    <SharedInfoTip content={
                      "The retailer's own commission figure for this brand's lines on this order, as it "
                      + 'reported them. This is what the retailer says it is deducting — not 2W’s '
                      + 'calculation from a configured rate, which is the Marketplace Commission column '
                      + 'on Orders Management and can differ.'
                    } />
                  </InlineStack>
                  <Text variant="headingMd">{money(selectedOrder.totalCommission, selectedOrder.currencyCode)}</Text>
                </BlockStack>
                <BlockStack gap="100">
                  {/*
                    NOT "Net Payout". Mirakl never told us a payout.
                    MiraklOrder carries exactly two money columns — totalPrice and
                    totalCommission (schema.prisma:1290-1291) — and there is no payout field,
                    because OR11 gives us none. This figure is our own subtraction of one from
                    the other, so it ignores whatever else the retailer nets: taxes, shipping
                    charges, adjustments, any fee outside the commission line.
                    Naming it "Net Payout" asserted a figure the retailer never sent, on the
                    one screen where every other number IS the retailer's own.
                  */}
                  <InlineStack gap="100" blockAlign="center">
                    <Text variant="bodySm" tone="subdued">Price less commission</Text>
                    <SharedInfoTip content={
                      'Total price minus the retailer commission, calculated here. Mirakl does not '
                      + 'report a payout figure to us, so this is arithmetic on the two amounts it '
                      + 'does report — not what the retailer will actually pay, which can also net '
                      + 'tax, shipping and other charges.'
                    } />
                  </InlineStack>
                  <Text variant="headingMd">
                    {money((selectedOrder.totalPrice || 0) - (selectedOrder.totalCommission || 0), selectedOrder.currencyCode)}
                  </Text>
                </BlockStack>
              </InlineStack>
              <Divider />

              {/* Tracking */}
              <Text variant="headingSm" as="h4">Tracking</Text>
              {selectedOrder.trackingNumber ? (
                <InlineStack gap="600" wrap>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Carrier</Text>
                    <Text variant="bodyMd">{selectedOrder.carrier || '—'}</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Tracking Number</Text>
                    {selectedOrder.trackingUrl ? (
                      <Link url={selectedOrder.trackingUrl} target="_blank">{selectedOrder.trackingNumber}</Link>
                    ) : (
                      <Text variant="bodyMd">{selectedOrder.trackingNumber}</Text>
                    )}
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Shipped</Text>
                    <Text variant="bodyMd">
                      {selectedOrder.shippedAt ? new Date(selectedOrder.shippedAt).toLocaleString() : '—'}
                    </Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text variant="bodySm" tone="subdued">Pushed to Mirakl</Text>
                    {selectedOrder.trackingPushedAt ? (
                      <Badge tone="success">{new Date(selectedOrder.trackingPushedAt).toLocaleString()}</Badge>
                    ) : (
                      <Badge tone="attention">Not pushed</Badge>
                    )}
                  </BlockStack>
                </InlineStack>
              ) : (
                <Text tone="subdued">No tracking information yet</Text>
              )}

              {/* Error */}
              {selectedOrder.error && (
                <>
                  <Divider />
                  <Banner tone="critical">{selectedOrder.error}</Banner>
                </>
              )}
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}

      {/* Queue and Sync History used to sit here, as two cards at the bottom of this tab.
          They are top-level brand tabs now, for two reasons: work that has failed was at the
          bottom of the longest tab on the page, and both tables carry BILLING runs as well as
          connector ones — so they sat under a heading naming the wrong half of what they
          hold. Neither is scoped to Retail Connect's subject, and neither belongs to it. */}
    </BlockStack>
  );
}
